import axios from 'axios';

import routesPath from '@/shared/constants/routesPath';
import { clearSession, getSession } from '@/shared/store/session';

import {
  ensureValidToken,
  refreshTokens,
} from '@/services/tokenRefreshService';

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Track retry attempts for 401 errors
const MAX_RETRY_ATTEMPTS = 3;
const retryCountMap = new Map();

// Request interceptor: add auth header and refresh token if needed
instance.interceptors.request.use(
  async (config) => {
    config.headers['apiKey'] = import.meta.env.VITE_API_KEY;
    config.headers['Content-Type'] = 'application/json';

    // Skip auth refresh logic for refresh-token requests or flagged calls
    if (config.skipAuthRefresh) {
      return config;
    }

    // Only add auth header if session exists
    const session = getSession();
    if (session) {
      try {
        // Ensure token is valid before making request
        const validSession = await ensureValidToken();
        if (validSession && validSession.access_token) {
          const tokenType = validSession.token_type || 'Bearer';
          config.headers.Authorization = `${tokenType} ${validSession.access_token}`;
        }
      } catch (error) {
        // If refresh fails, clear session and redirect to login
        console.error('Token refresh failed in request interceptor:', error);
        // Don't throw here, let the request proceed and handle in response interceptor
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: handle 401 errors with automatic retry
instance.interceptors.response.use(
  (response) => {
    // Clear retry count on successful response
    const requestKey = `${response.config.method}-${response.config.url}`;
    retryCountMap.delete(requestKey);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (originalRequest?.skipAuthRefresh) {
      return Promise.reject(error);
    }
    const requestKey = `${originalRequest.method}-${originalRequest.url}`;
    const retryCount = retryCountMap.get(requestKey) || 0;

    // Handle 401 errors (only if session exists)
    if (error.response?.status === 401) {
      const session = getSession();
      if (session && retryCount < MAX_RETRY_ATTEMPTS) {
        try {
          // Attempt to refresh token
          await refreshTokens();
          // Update retry count
          retryCountMap.set(requestKey, retryCount + 1);
          // Retry original request with new token
          const updatedSession = getSession();
          if (updatedSession?.access_token) {
            const tokenType = updatedSession.token_type || 'Bearer';
            originalRequest.headers.Authorization = `${tokenType} ${updatedSession.access_token}`;
            return instance(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed, clear session and redirect to login
          console.error(
            'Token refresh failed in response interceptor:',
            refreshError
          );
          retryCountMap.delete(requestKey);
          // Clear session
          clearSession();
          // Redirect to login
          if (window.location.pathname !== routesPath.LOGIN) {
            window.location.href = routesPath.LOGIN;
          }
          return Promise.reject(refreshError);
        }
      } else if (session && retryCount >= MAX_RETRY_ATTEMPTS) {
        // Retries exhausted, clear session and redirect to login
        console.error('Max retry attempts reached for 401 error');
        retryCountMap.delete(requestKey);
        clearSession();
        // Redirect to login
        if (window.location.pathname !== routesPath.LOGIN) {
          window.location.href = routesPath.LOGIN;
        }
        return Promise.reject(error);
      } else if (!session) {
        // No session, return error normally (might be public endpoint)
        retryCountMap.delete(requestKey);
        return Promise.reject(error);
      }
    }

    // Handle 403 errors
    if (error.response?.status === 403) {
      // Could redirect to unauthorized page if needed
      console.error('Access forbidden');
    }

    // Clear retry count for non-401 errors
    retryCountMap.delete(requestKey);
    return Promise.reject(error);
  }
);

export default instance;
